import { Modal } from '@cointracker/styleguide';
import { Body2, Body4, Button } from '@cointracker/styleguide/src/LoggedIn';
import { UploadSimpleIcon } from '@phosphor-icons/react';
import { useRef, useState } from 'react';
import { TaxPayerCase, useTaxCaseStore } from '../TaxProClients/context';

interface CSVImportModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  onImport: (cases: TaxPayerCase[]) => void;
}

export function CSVImportModal({ isOpen, onDismiss }: CSVImportModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<TaxPayerCase[]>([]);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { addCase } = useTaxCaseStore();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setError(null);

    try {
      const content = await selectedFile.text();
      const importedCases = parseCSV(content);
      setPreviewData(importedCases);
    } catch (err) {
      setError(
        'Failed to parse CSV file. Please check the format and try again.',
      );
      console.error(err);
    }
  };

  const parseCSV = (content: string): TaxPayerCase[] => {
    const lines = content.split('\n');
    if (lines.length <= 1) {
      throw new Error('CSV file is empty or has only headers');
    }

    const headers = lines[0].split(',').map((h) => h.trim());

    const requiredHeaders = ['caseName', 'taxYears'];
    const missingHeaders = requiredHeaders.filter((h) => !headers.includes(h));

    if (missingHeaders.length > 0) {
      throw new Error(`Missing required headers: ${missingHeaders.join(', ')}`);
    }

    const cases: TaxPayerCase[] = [];

    for (let i = 1; i < lines.length; i++) {
      if (!lines[i].trim()) continue; // Skip empty lines

      const values: string[] = [];
      let currentValue = '';
      let inQuotes = false;
      const chars = lines[i].split('');

      for (let j = 0; j < chars.length; j++) {
        const char = chars[j];

        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(currentValue.trim());
          currentValue = '';
        } else {
          currentValue += char;
        }
      }

      values.push(currentValue.trim());

      if (values.length !== headers.length) {
        console.warn(
          `Line ${i + 1} has incorrect number of values (${values.length} vs ${headers.length} expected). Skipping.`,
        );
        continue;
      }

      const caseData: Partial<TaxPayerCase> = {};

      headers.forEach((header, index) => {
        let value = values[index];
        if (value.startsWith('"') && value.endsWith('"')) {
          value = value.substring(1, value.length - 1);
        }

        if (header === 'status') {
          const statusValue = value as TaxPayerCase['status'];
          if (
            !['Completed', 'Not started', 'Canceled', 'In progress'].includes(
              statusValue,
            )
          ) {
            caseData[header] = 'Not started';
          } else {
            caseData[header] = statusValue;
          }
        } else if (header === 'taxYears') {
          // Parse comma-separated years
          const years = value
            .split(',')
            .map((y) => parseInt(y.trim(), 10))
            .filter((y) => !isNaN(y));
          caseData[header] = years;
        } else {
          caseData[header] = value;
        }
      });

      if (caseData.caseName && caseData.taxYears) {
        cases.push({
          caseName: caseData.caseName,
          taxYears: caseData.taxYears,
          status: caseData.status || 'Not started',
          notes: caseData.notes || '',
        });
      }
    }

    return cases;
  };

  const handleImport = () => {
    if (previewData.length > 0) {
      previewData.forEach((caseData) => {
        addCase(caseData);
      });
      onDismiss();
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const droppedFile = e.dataTransfer?.files?.[0];
    if (!droppedFile || !droppedFile.name.endsWith('.csv')) {
      setError('Please upload a CSV file');
      return;
    }

    setFile(droppedFile);
    setError(null);

    try {
      const content = await droppedFile.text();
      const importedCases = parseCSV(content);
      setPreviewData(importedCases);
    } catch (err) {
      setError(
        'Failed to parse CSV file. Please check the format and try again.',
      );
      console.error(err);
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={(open) => !open && onDismiss()}>
      <Modal.Content className="min-w-[600px]">
        <Modal.ContentHeader title="Import Tax Payer Cases" />
        <Modal.ContentBody>
          {!file ? (
            <button
              className="flex w-full flex-col items-center justify-center rounded-8 border-2 border-dashed border-line-tertiary p-40 text-left transition-colors hover:border-line-primary focus:outline-none focus:ring-2 focus:ring-line-primary"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  fileInputRef.current?.click();
                }
              }}
              type="button"
              aria-label="Upload CSV file"
            >
              <input
                type="file"
                ref={fileInputRef}
                accept=".csv"
                onChange={handleFileChange}
                className="hidden"
              />
              <UploadSimpleIcon
                size={32}
                className="text-text-tertiary mb-12"
                weight="bold"
              />
              <Body2 className="font-medium text-text-primary">
                Drag and drop a CSV file, or click to browse
              </Body2>
              <Body2 className="mb-16 text-text-secondary">
                CSV must include caseName, taxYears columns. For notes with
                commas or quotes, wrap values in double quotes. Tax years should
                be comma-separated numbers (e.g., &quot;2023,2024&quot;).
              </Body2>
            </button>
          ) : (
            <div className="flex flex-col gap-16">
              <div className="flex items-center justify-between">
                <div>
                  <Body2 className="font-medium text-text-primary">
                    {file.name}
                  </Body2>
                  <Body4 className="text-text-tertiary">
                    {previewData.length} cases found
                  </Body4>
                </div>
                <Button
                  variant="line"
                  onClick={() => {
                    setFile(null);
                    setPreviewData([]);
                    setError(null);
                  }}
                >
                  Change file
                </Button>
              </div>

              {error && (
                <div className="rounded-8 bg-background-negative p-12">
                  <Body4 className="text-text-negative">{error}</Body4>
                </div>
              )}

              {previewData.length > 0 && (
                <div className="max-h-[300px] overflow-auto rounded-8 border border-line-card">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="text-xs border-b border-line-card font-medium text-text-secondary">
                        <th className="p-12 text-left font-medium">
                          Case Name
                        </th>
                        <th className="p-12 text-left font-medium">Status</th>
                        <th className="p-12 text-left font-medium">
                          Tax Years
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.map((item, index) => (
                        <tr
                          key={index}
                          className="border-b border-line-card last:border-0"
                        >
                          <td className="p-12">{item.caseName}</td>
                          <td className="p-12">{item.status}</td>
                          <td className="p-12">
                            {item.taxYears.length > 0
                              ? item.taxYears.sort((a, b) => b - a).join(', ')
                              : 'No years selected'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </Modal.ContentBody>
        <Modal.ContentFooter>
          <Button variant="line" onClick={onDismiss}>
            Cancel
          </Button>
          <Button disabled={previewData.length === 0} onClick={handleImport}>
            Import {previewData.length} Cases
          </Button>
        </Modal.ContentFooter>
      </Modal.Content>
    </Modal.Root>
  );
}
